import AutoNumeric from 'autonumeric';
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartSettingsPopupModel } from "../models/chart-style/chart-settings-popup.model";
import { ChartStyleSettingsModel } from '../models/chart-style/chart-style-settings.model';
import { TradeAccountModel } from '../models/trade/trade-account.model';
import { QuoteType } from '../models/trade/trade.model';
import { IComponent } from "./component.interface";
import { TradeOrderTypeComponent } from './menu/trade-order-type.component';
import { PnlComponent } from './pnl.component';
import { TradeComponent } from './trade.component';
import { CurrentPositionViewModel } from '../models/pnl/current-position-view-model';
import { TradeOrderDataModel } from '../models/draw-trade-order/trade-order-data.model';

export class ChatSettingsPopupComponent implements IComponent {
    public id = '';

    private element: HTMLElement;
    private popup: HTMLElement | null = null;
    private base: IChartContainer;

    private quoteQuantityInput: AutoNumeric | null = null;

    private inited = false;
    private styleLoaded = false;
    private accountLoaded = false;
    private accounts: Array<TradeAccountModel> = [];
    private style: ChartStyleSettingsModel | null = null;
    private hasAnyPositions = false;
    private hasAnyLimitOrders = false;

    constructor(element: HTMLElement, base: IChartContainer) {
        this.element = element
        this.base = base;

        this.base.chartService.subscribeChartStyleData((style: ChartStyleSettingsModel) => {
            setTimeout(() => {
                this.styleLoaded = true;

                if (this.inited) {
                    if (!this.base.marketingWebChartMode) {
                        this.validateAndSetDefaultTradeSize();
                    }

                    return;
                }

                this.style = style;
                this.createPopup();
            }, 200);
        });
        this.base.chartService.subscribe_TradeAccounts((accounts: Array<TradeAccountModel>) => {
            this.accountLoaded = true;
            this.accounts = accounts;

            if (this.inited) {
                this.updateAccountHtmlList();
                return
            }

            this.createPopup();
        });

        this.base.chartService.subscribeCurrentPositionViewModel((data: Array<CurrentPositionViewModel>) => {
            this.hasAnyPositions = data.length > 0;
        });

        this.base.chartService.subscribeDrawTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            this.hasAnyLimitOrders = tradeOrders.filter(x => x.tradeType?.toLowerCase() != 'market').length > 0;
        });

        this.onResize();
    }

    private createPopup() {
        if (!this.styleLoaded || !this.accountLoaded) {
            return;
        }

        this.inited = true;

        if (!this.style?.chartSettingsPopupEnabled) {
            return;
        }

        this.create(this.style);
        this.onClick();
        this.onBodyClick();
    }

    private updateAccountHtmlList() {
        if (this.popup?.containClass('active')) return;

        const html = this.getAccountCheckBoxHtml();
        const accountTab = this.popup?.querySelector<HTMLElement>('.content-tab.accounts');
        if (accountTab) {
            accountTab.innerHTML = html;
        }
    }

    private onClick() {
        this.element.addEventListener('click', () => {
            this.show();
        });

        this.popup?.querySelectorAll<HTMLElement>('.submit').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                this.setValueBeforeClose();

                this.close();
            });
        });
        this.popup?.querySelectorAll<HTMLElement>('.close').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                this.close();
            });
        });
        this.popup?.querySelectorAll<HTMLElement>('.section').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                el.classList.toggle('active');
            });
        });
    }

    private setValueBeforeClose() {
        let model = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
        if (!model) {
            model = new ChartSettingsPopupModel();
        }

        const checkboxDisplayConfirmPopup = this.popup?.querySelector<HTMLInputElement>('#chart-settings-dont-show-again');
        if (checkboxDisplayConfirmPopup) {
            const shouldDisplayConfirmPopup = checkboxDisplayConfirmPopup.checked;
            model.displayOrderConfirmationPopup = shouldDisplayConfirmPopup;
        }

        const checkboxDisplayMoneyMachineConfirmPopup = this.popup?.querySelector<HTMLInputElement>('#chart-settings-dont-show-money-machine-again');
        if (checkboxDisplayMoneyMachineConfirmPopup) {
            const shouldDisplayMoneyMachineConfirmPopup = checkboxDisplayMoneyMachineConfirmPopup.checked;
            model.displayMoneyMachineConfirmationPopup = shouldDisplayMoneyMachineConfirmPopup;
        }

        if (this.base.tiChartSeries.isPreMarketEnabled()) {
            const checkboxShowPremarket = this.popup?.querySelector<HTMLInputElement>('#chart-settings-show-per-market');
            const shouldShowPremarket = checkboxShowPremarket?.checked ?? false;
            model.displayPremarket = shouldShowPremarket;
        }

        if (this.base.tiChartSeries.isPostMarketEnabled()) {
            const checkboxShowPostmarket = this.popup?.querySelector<HTMLInputElement>('#chart-settings-show-post-market');
            const shouldShowPostmarket = checkboxShowPostmarket?.checked ?? false;
            model.displayPostmarket = shouldShowPostmarket;
        }

        const checkboxDisplayZoomAndScroll = this.popup?.querySelector<HTMLInputElement>('#chart-settings-zoom-and-scroll');
        if (checkboxDisplayZoomAndScroll) {
            const shouldDisplayZoomAndScroll = checkboxDisplayZoomAndScroll.checked;
            model.displayChartSettingZoomAndScroll = shouldDisplayZoomAndScroll;
        }

        const checkboxDisplayMainChartChangePercent = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-main-chart-change-percent');
        if (checkboxDisplayMainChartChangePercent) {
            const shouldDisplayMainChartChangePercent = checkboxDisplayMainChartChangePercent.checked;
            model.displayMainChartChangePercentVisibleCandles = shouldDisplayMainChartChangePercent;
        }

        const checkboxDisplayBrowserChartExistingBuySellButtons = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-browsre-chart-existing-buy-sell-buttons');
        if (checkboxDisplayBrowserChartExistingBuySellButtons) {
            const shouldDisplayBrowserChartExistingBuySellButtons = checkboxDisplayBrowserChartExistingBuySellButtons.checked;
            model.displayBrowserChartExistingBuySellButtons = shouldDisplayBrowserChartExistingBuySellButtons;
        }

        const checkboxDisplayAlertsOnChart = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-alerts-on-chart');
        if (checkboxDisplayAlertsOnChart) {
            const shouldDisplayAlertsOnChart = checkboxDisplayAlertsOnChart.checked;
            model.displayAlertsOnChart = shouldDisplayAlertsOnChart;
        }

        const checkBoxDisplayResistanceAndSupport = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-resistance-and-support');
        if (checkBoxDisplayResistanceAndSupport) {
            const shouldDisplayResistanceAndSupport = checkBoxDisplayResistanceAndSupport.checked;
            model.displayResistanceAndSupport = shouldDisplayResistanceAndSupport;
        }

        const checkboxDisplayNewIndicatorManager = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-new-indicator-manager');
        if (checkboxDisplayNewIndicatorManager) {
            const shouldDisplayNewIndicatorManager = checkboxDisplayNewIndicatorManager.checked;
            model.displayNewIndicatorManager = shouldDisplayNewIndicatorManager;
        }

        const checkboxDefaultUseTradeOrderEntry = this.popup?.querySelector<HTMLInputElement>('#chart-settings-default-use-trade-order-entry');
        if (checkboxDefaultUseTradeOrderEntry) {
            const shouldDefaultUseTradeOrderEntry = checkboxDefaultUseTradeOrderEntry.checked;
            model.defaultEnableTradeOrderEntry = shouldDefaultUseTradeOrderEntry;
        }

        const checkboxDisplayBuySellFlags = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-buy-sell-flags');
        if (checkboxDisplayBuySellFlags) {
            const shouldDisplayBuySellFlags = checkboxDisplayBuySellFlags.checked;
            model.displayChartSettingBuySellFlags = shouldDisplayBuySellFlags;
        }

        const checkboxPnPBrowserChartsByDefault = this.popup?.querySelector<HTMLInputElement>('#chart-settings-pnp-browser-charts-by-default');
        if (checkboxPnPBrowserChartsByDefault) {
            const shouldDisplayPnPBrowserChartsByDefault = checkboxPnPBrowserChartsByDefault.checked;
            model.pnpBrowserChartsByDefault = shouldDisplayPnPBrowserChartsByDefault;
        }

        const checkboxEnableRightEdgePadding = this.popup?.querySelector<HTMLInputElement>('#chart-settings-enable-right-edge-padding');
        if (checkboxEnableRightEdgePadding) {
            const shouldDisplayRightEdgePadding = checkboxEnableRightEdgePadding.checked;
            model.enableRightEdgePadding = shouldDisplayRightEdgePadding;
        }

        const checkboxApplyAllCharts = this.popup?.querySelector<HTMLInputElement>('#apply-all-charts');
        if (checkboxApplyAllCharts) {
            const shouldApplyAllCharts = checkboxApplyAllCharts.checked;
            model.applyToAllCharts = shouldApplyAllCharts;
        }

        const checkboxSaveToDefaults = this.popup?.querySelector<HTMLInputElement>('#save-to-defaults');
        if (checkboxSaveToDefaults) {
            const shouldSaveToDefaults = checkboxSaveToDefaults.checked;
            model.saveToDefaultCharts = shouldSaveToDefaults;
        }

        const defaultTradeSizeOption = this.popup?.querySelector<HTMLElement>('.default-trade-size li.selected');
        if (defaultTradeSizeOption) {
            model.defaultQuoteType = defaultTradeSizeOption.getAttribute('data-value') as QuoteType;
        }

        if (this.quoteQuantityInput) {
            model.defaultQuoteQuantity = this.quoteQuantityInput.getNumber();
        }

        if (this.base.tiChartSeries) {
            this.base.tiChartSeries.hiddenAccounts = [];
            this.popup?.querySelectorAll<HTMLElement>('.checkbox-input.account').forEach((el: HTMLElement) => {
                const checkbox = el.querySelector<HTMLInputElement>('input[type="checkbox"]');
                if (checkbox) {
                    const isChecked = checkbox.checked;
                    const accountName = el.getAttribute('data-value');
                    if (accountName && !isChecked) {
                        this.base.tiChartSeries.hiddenAccounts.push(accountName);
                    }
                }
            });

            this.base.chartService.notifyTradeAccountSubscribers(this.accounts);
        }

        this.base.chartService.requestSaveChartSettingsPopupModel(model);

        if (this.base.tiChartSeries?.chartStyleSettings) {
            this.base.tiChartSeries.chartStyleSettings.chartSettingsPopupModel = model;
            this.base.tiChartSeries.onRedraw();
        }

        this.updateDefaultTradeSize();
    }

    private updateDefaultTradeSize() {
        const model = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
        if (model) {
            const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
            const tradeComponent = this.base.getComponentsByType(TradeComponent)?.[0] as TradeComponent;
            const tradeOrderTypeComponents = this.base.getComponentsByType(TradeOrderTypeComponent) as Array<TradeOrderTypeComponent>;
            if (pnl) {
                pnl.preSelectedQuoteType = model.defaultQuoteType;
                pnl.preSelectedShares = model.defaultQuoteQuantity;
            }

            if (tradeComponent && pnl && !pnl.selectedAccount) {
                tradeComponent.setQuoteQuantity(model.defaultQuoteQuantity);
                tradeComponent.setQuoteType(model.defaultQuoteType);
                tradeOrderTypeComponents.forEach((x) => {
                    x.syncValue();
                });
            }
        }
    }

    private validateAndSetDefaultTradeSize() {
        const model = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
        if (model) {
            const defaultQuoteType = this.popup?.querySelector<HTMLElement>('.default-trade-size li.selected')?.getAttribute('data-value') as QuoteType;

            if (this.quoteQuantityInput?.getNumber() != model.defaultQuoteQuantity || defaultQuoteType != model.defaultQuoteType) {
                this.popup?.querySelectorAll<HTMLElement>('.default-trade-size li').forEach((el: HTMLElement) => {
                    el.classList.remove('selected');

                    if (el.getAttribute('data-value') == model.defaultQuoteType) {
                        el.classList.add('selected');
                    }
                });

                this.quoteQuantityInput?.set(model.defaultQuoteQuantity);
                this.updateDefaultTradeSize();
            }
        }
    }

    private show() {
        const model = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
        if (model) {
            const checkboxDontShowAgain = this.popup?.querySelector<HTMLInputElement>('#chart-settings-dont-show-again');
            if (checkboxDontShowAgain) {
                checkboxDontShowAgain.checked = true;
                if (!model.displayOrderConfirmationPopup) {
                    checkboxDontShowAgain.checked = false;
                }
            }

            const checkboxDontShowMoneyMachineAgain = this.popup?.querySelector<HTMLInputElement>('#chart-settings-dont-show-money-machine-again');
            if (checkboxDontShowMoneyMachineAgain) {
                checkboxDontShowMoneyMachineAgain.checked = true;
                if (!model.displayMoneyMachineConfirmationPopup) {
                    checkboxDontShowMoneyMachineAgain.checked = false;
                }
            }

            if (this.base.tiChartSeries.isPreMarketEnabled()) {
                const checkboxShowPremarket = this.popup?.querySelector<HTMLInputElement>('#chart-settings-show-per-market');
                if (checkboxShowPremarket) {
                    checkboxShowPremarket.checked = model.displayPremarket;
                }
            }

            if (this.base.tiChartSeries.isPostMarketEnabled()) {
                const checkboxShowPostmarket = this.popup?.querySelector<HTMLInputElement>('#chart-settings-show-post-market');
                if (checkboxShowPostmarket) {
                    checkboxShowPostmarket.checked = model.displayPostmarket;
                }
            }

            const checkboxDisplayChartSettingZoomAndScroll = this.popup?.querySelector<HTMLInputElement>('#chart-settings-zoom-and-scroll');
            if (checkboxDisplayChartSettingZoomAndScroll) {
                checkboxDisplayChartSettingZoomAndScroll.checked = false;
                if (model.displayChartSettingZoomAndScroll) {
                    checkboxDisplayChartSettingZoomAndScroll.checked = true;
                }
            }

            const checkboxDisplayPnPBrowserChartsByDefault = this.popup?.querySelector<HTMLInputElement>('#chart-settings-pnp-browser-charts-by-default');
            if (checkboxDisplayPnPBrowserChartsByDefault) {
                checkboxDisplayPnPBrowserChartsByDefault.checked = false;
                if (model.pnpBrowserChartsByDefault) {
                    checkboxDisplayPnPBrowserChartsByDefault.checked = true;
                }
            }

            const checkboxDisplayRightEdgePadding = this.popup?.querySelector<HTMLInputElement>('#chart-settings-enable-right-edge-padding');
            if (checkboxDisplayRightEdgePadding) {
                checkboxDisplayRightEdgePadding.checked = false;
                if (model.enableRightEdgePadding) {
                    checkboxDisplayRightEdgePadding.checked = true;
                }
            }

            const checkboxDisplayMainChartChangePercent = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-main-chart-change-percent');
            if (checkboxDisplayMainChartChangePercent) {
                checkboxDisplayMainChartChangePercent.checked = false;
                if (model.displayMainChartChangePercentVisibleCandles) {
                    checkboxDisplayMainChartChangePercent.checked = true;
                }
            }

            const checkboxDisplayBrowserChartExistingBuySellButtons = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-browsre-chart-existing-buy-sell-buttons');
            if (checkboxDisplayBrowserChartExistingBuySellButtons) {
                checkboxDisplayBrowserChartExistingBuySellButtons.checked = false;
                if (model.displayBrowserChartExistingBuySellButtons) {
                    checkboxDisplayBrowserChartExistingBuySellButtons.checked = true;
                }
            }

            const checkboxDisplayAlertsOnChart = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-alerts-on-chart');
            if (checkboxDisplayAlertsOnChart) {
                checkboxDisplayAlertsOnChart.checked = false;
                if (model.displayAlertsOnChart) {
                    checkboxDisplayAlertsOnChart.checked = true;
                }
            }

            const checkBoxDisplayResistanceAndSupport = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-resistance-and-support');
            if (checkBoxDisplayResistanceAndSupport) {
                checkBoxDisplayResistanceAndSupport.checked = false;
                if (model.displayResistanceAndSupport) {
                    checkBoxDisplayResistanceAndSupport.checked = true;
                }
            }

            const checkboxDisplayNewIndicatorManager = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-new-indicator-manager');
            if (checkboxDisplayNewIndicatorManager) {
                checkboxDisplayNewIndicatorManager.checked = false;
                if (model.displayNewIndicatorManager) {
                    checkboxDisplayNewIndicatorManager.checked = true;
                }
            }

            const checkboxDefaultUseTradeOrderEntry = this.popup?.querySelector<HTMLInputElement>('#chart-settings-default-use-trade-order-entry');
            if (checkboxDefaultUseTradeOrderEntry) {
                checkboxDefaultUseTradeOrderEntry.checked = false;
                if (model.defaultEnableTradeOrderEntry) {
                    checkboxDefaultUseTradeOrderEntry.checked = true;
                }
            }

            const checkboxDisplayChartSettingBuySellFlags = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-buy-sell-flags');
            if (checkboxDisplayChartSettingBuySellFlags) {
                checkboxDisplayChartSettingBuySellFlags.checked = false;
                if (model.displayChartSettingBuySellFlags) {
                    checkboxDisplayChartSettingBuySellFlags.checked = true;
                }
            }

            const checkboxApplyAllCharts = this.popup?.querySelector<HTMLInputElement>('#apply-all-charts');
            if (checkboxApplyAllCharts) {
                checkboxApplyAllCharts.checked = false;
            }

            const checkboxSaveToDefaults = this.popup?.querySelector<HTMLInputElement>('#save-to-defaults');
            if (checkboxSaveToDefaults) {
                checkboxSaveToDefaults.checked = false;
                if (model.saveToDefaultCharts) {
                    checkboxSaveToDefaults.checked = true;
                }
            }

            if (this.quoteQuantityInput) {
                if (model.defaultQuoteQuantity > 0) {
                    this.quoteQuantityInput.set(model.defaultQuoteQuantity)
                }
            }

            const defaultTradeSizeOptions = this.popup?.querySelectorAll<HTMLElement>('.default-trade-size li');
            const defaultTradeSizeLabel = this.popup?.querySelector<HTMLElement>('.default-trade-size .selected p');
            if (defaultTradeSizeOptions?.length && defaultTradeSizeLabel) {
                let labelValue = '';
                defaultTradeSizeOptions.forEach((opt: HTMLElement) => {
                    if (model.defaultQuoteType == opt.getAttribute('data-value')) {
                        opt.classList.add('selected');
                        labelValue = opt.innerHTML;
                    } else {
                        opt.classList.remove('selected');
                    }
                });


                if (labelValue) {
                    defaultTradeSizeLabel.innerHTML = labelValue;
                }
            }
        }

        this.activeFirstTab();
        this.setRightEdgePaddingCheckboxState();
        this.popup?.addClass('active');
    }

    private activeFirstTab() {
        const tabs = this.popup?.querySelectorAll<HTMLElement>('.tabs .tab') ?? [];
        const contents = this.popup?.querySelectorAll<HTMLElement>('.content .content-tab') ?? [];

        tabs.forEach((tab) => {
            tab.classList.remove('active');
        });

        contents.forEach((content) => {
            content.classList.remove('active');
        });

        tabs[0]?.classList.add('active');
        contents[0]?.classList.add('active');
    }

    private close() {
        this.popup?.removeClass('active');
    }

    private closePicker() {
        const tradeTypePickerElement = this.popup?.querySelector('.font-size-picker-wrapper');
        if (tradeTypePickerElement) {
            tradeTypePickerElement.classList.remove('active');
        }
    }

    private onPickerClick() {
        const pickerElement = this.popup?.querySelector('.font-size-picker-wrapper');
        if (pickerElement) {
            pickerElement.addEventListener('click', () => {
                pickerElement.classList.toggle('active');
            });

            const options = pickerElement.querySelectorAll<HTMLElement>('li');

            options.forEach((li: HTMLElement) => {
                li.addEventListener('click', () => {
                    options.forEach((opt: HTMLElement) => {
                        opt.classList.remove('selected');
                    });

                    li.classList.add('selected');
                    const label = pickerElement.querySelector('.selected p');
                    if (label) {
                        label.innerHTML = li.innerHTML;
                    }
                });
            });
        }
    }

    private create(style: ChartStyleSettingsModel) {
        this.element.classList.remove('hidden');
        const div = document.createElement('div');
        div.className = `chart-settings settings-popup ${this.base.marketingWebChartMode ? '' : 'tipro'}`;
        const accountCheckBoxHtml = this.getAccountCheckBoxHtml();

        if (this.base.marketingWebChartMode) {
            div.innerHTML = `
            <div class="wrapper">
                <div class="head">
                    <p class="title">Chart Settings</p>
                    <button class="close" type="button">
                        <span class="icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" width="17" height="17" fill="currentColor">
                                <path d="m.58 1.42.82-.82 15 15-.82.82z"></path>
                                <path d="m.58 15.58 15-15 .82.82-15 15z"></path>
                            </svg>
                        </span>
                    </button>
                </div>
                <div class="content" style="overflow: visible; max-height: ${this.base.chartWrapper?.clientHeight - 150}px;">
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-dont-show-again" />
                        <label for="chart-settings-dont-show-again">Display Order Confirmation</label>
                    </div>
                    <div class="checkbox-input ${this.base.tiChartSeries.isPreMarketDisabled() ? 'hidden' : ''}">
                        <input type="checkbox" class="mgr-8" id="chart-settings-show-per-market" />
                        <label for="chart-settings-show-per-market">Display Premarket</label>
                    </div>
                    <div class="checkbox-input  ${this.base.tiChartSeries.isPostMarketDisabled() ? 'hidden' : ''}">
                        <input type="checkbox" class="mgr-8" id="chart-settings-show-post-market" />
                        <label for="chart-settings-show-post-market">Display Postmarket</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-zoom-and-scroll" />
                        <label for="chart-settings-zoom-and-scroll">Display Zoom and Scroll Controls</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-display-main-chart-change-percent" />
                        <label for="chart-settings-display-main-chart-change-percent">Display main chart Change Percent</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-display-buy-sell-flags" />
                        <label for="chart-settings-display-buy-sell-flags">Display Buy Sell Flags</label>
                    </div>
                    <hr/>
                    <div class="control default-trade-size">
                        <span class="label-inline" style="width: 120px; margin-right: 4px;">Default Trade Size</span>
                        <div class="font-size-picker-wrapper mgr-4">
                            <div class="font-size-picker">
                                <div class="selected">
                                    <p>Shares</p>
                                </div>
                                <div class="arrow">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11 7" width="11" height="7"
                                        fill="none">
                                        <path stroke="currentColor" stroke-width="1.3" d="M.5 1.5l5 4 5-4"></path>
                                    </svg>
                                </div>
                                <ul class="selection">
                                    <li data-value="Shares" class="selected">Shares</li>
                                    <li data-value="Dollars">Dollars</li>
                                </ul>
                            </div>
                        </div>
                        <div class="text-input">
                            <input type="text" inputmode="decimal" pattern="[0-9]*" value="" />
                        </div>
                    </div>
                </div>
                <div class="footer">
                    <button class="close">Close</button>
                    <button class="submit">Ok</button>
                </div>
            </div>`;
        } else {
            const tradeEnabled = style.tradeOrderEntryEnabled && style.tradeOrderEntrySupported;
            const indicatorEnabled = style.webBasedIndicatorManagerSupported;
            const alertsOnChartEnabled = style.alertsOnChartSupported;
            const resistanceSupported = style.constantResistanceSupported && style.constantResistanceEnabled;
            // const chartSettingsWorkflowSupported = style.chartSettingsWorkflowSupported;
            const chartSettingsWorkflowSupported = false;
            const moneyMachineSupported = style.moneyMachineSupported;
            div.innerHTML = `
            <div class="wrapper">
                <div class="head"> 
                    <div class="tabs">
                        <div class="tab active">
                            <p class="title">Chart Settings</p>
                        </div>
                        <div class="tab">
                            <p class="title">Account Settings</p>
                        </div>
                    </div>
                    <button class="close" type="button">
                        <span class="icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" width="17" height="17" fill="currentColor">
                                <path d="m.58 1.42.82-.82 15 15-.82.82z"></path>
                                <path d="m.58 15.58 15-15 .82.82-15 15z"></path>
                            </svg>
                        </span>
                    </button>
                </div>
                <div class="content" style="overflow-y: auto; max-height: ${this.base.chartWrapper?.clientHeight - 150}px;">
                    <div class="content-tab active">
                        <div class="checkbox-input">
                            <input type="checkbox" class="mgr-8" id="chart-settings-dont-show-again" />
                            <label for="chart-settings-dont-show-again">Display Order Confirmation</label>
                        </div>
                        <div class="checkbox-input ${moneyMachineSupported ? `` : `hidden`}">
                            <input type="checkbox" class="mgr-8" id="chart-settings-dont-show-money-machine-again" />
                            <label for="chart-settings-dont-show-money-machine-again">Display Money Machine Confirmation</label>
                        </div>
                        <div class="checkbox-input">           
                            <input type="checkbox" class="mgr-8" id="chart-settings-zoom-and-scroll" />
                            <label for="chart-settings-zoom-and-scroll">Display Zoom and Scroll Controls</label>
                        </div>
                        <div class="checkbox-input">
                            <input type="checkbox" class="mgr-8" id="chart-settings-display-main-chart-change-percent" />
                            <label for="chart-settings-display-main-chart-change-percent">Display main chart Change Percent</label>
                        </div>
                        <div class="checkbox-input">
                            <input type="checkbox" class="mgr-8" id="chart-settings-display-buy-sell-flags" />
                            <label for="chart-settings-display-buy-sell-flags">Display Buy Sell Flags</label>
                        </div>
                        <div class="checkbox-input">
                            <input type="checkbox" class="mgr-8" id="chart-settings-enable-right-edge-padding" />
                            <label for="chart-settings-enable-right-edge-padding">Enable Right-Edge Padding</label>
                        </div>
                        <div class="checkbox-input">
                            <input type="checkbox" class="mgr-8" id="chart-settings-pnp-browser-charts-by-default" />
                            <label for="chart-settings-pnp-browser-charts-by-default">Use Browser Charts by Default</label>
                        </div>
                        <div class="checkbox-input ${indicatorEnabled ? `` : `hidden`}">
                            <input type="checkbox" class="mgr-8" id="chart-settings-display-new-indicator-manager" />
                            <label for="chart-settings-display-new-indicator-manager">Display New Indicator Manager</label>
                        </div>
                        <div class="checkbox-input ${tradeEnabled ? `` : `hidden`}">
                            <input type="checkbox" class="mgr-8" id="chart-settings-default-use-trade-order-entry" />
                            <label for="chart-settings-default-use-trade-order-entry">Enable Trade Order Entry</label>
                        </div> 
                        <div class="checkbox-input ${tradeEnabled ? `` : `hidden`}">
                            <input type="checkbox" class="mgr-8" id="chart-settings-display-browsre-chart-existing-buy-sell-buttons" />
                            <label for="chart-settings-display-browsre-chart-existing-buy-sell-buttons">Display Buy and Sell Buttons</label>
                        </div> 
                        <div class="checkbox-input ${alertsOnChartEnabled ? `` : `hidden`}">
                            <input type="checkbox" class="mgr-8" id="chart-settings-display-alerts-on-chart" />
                            <label for="chart-settings-display-alerts-on-chart">Display Alerts on Chart</label>
                        </div>
                        <div class="checkbox-input ${resistanceSupported ? `` : `hidden`}">
                            <input type="checkbox" class="mgr-8" id="chart-settings-display-resistance-and-support" />
                            <label for="chart-settings-display-resistance-and-support">Display Resistance & Support</label>
                        </div>
                        <hr class="${tradeEnabled ? `` : `hidden`}" />
                        <div class="control default-trade-size ${tradeEnabled ? `` : `hidden`}">
                            <span class="label-inline" style="width: 120px; margin-right: 4px;">Default Trade Size</span>
                            <div class="font-size-picker-wrapper mgr-4">
                                <div class="font-size-picker">
                                    <div class="selected">
                                        <p>Shares</p>
                                    </div>
                                    <div class="arrow">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11 7" width="11" height="7"
                                            fill="none">
                                            <path stroke="currentColor" stroke-width="1.3" d="M.5 1.5l5 4 5-4"></path>
                                        </svg>
                                    </div>
                                    <ul class="selection">
                                        <li data-value="Shares" class="selected">Shares</li>
                                        <li data-value="Dollars">Dollars</li>
                                    </ul>
                                </div>
                            </div>
                            <div class="text-input">
                                <input type="text" inputmode="decimal" pattern="[0-9]*" value="" />
                            </div>
                        </div>
                    </div>
                    <div class="content-tab accounts">
                        ${accountCheckBoxHtml}
                    </div>
                </div>
                <div class="footer">
                    <div class="footer-checkbox" style="${chartSettingsWorkflowSupported ? `display: block !important; top: 14px;` : ``}">
                        <input type="checkbox" id="apply-all-charts" />
                        <label for="apply-all-charts">Apply to all open charts</label>
                    </div>
                    <div class="footer-checkbox" style="${chartSettingsWorkflowSupported ? `display: block !important; top: 36px;` : ``}">
                        <input type="checkbox" id="save-to-defaults" />
                        <label for="save-to-defaults">Save as default</label>
                    </div>
                    <button class="close">Close</button>
                    <button class="submit">Ok</button>
                </div>
            </div>`;
        }

        this.popup = div;
        this.base.chartWrapper?.insertAdjacentElement('beforeend', this.popup);
        setTimeout(() => {
            this.onTabClick();
            this.onNextResistanceAndSupportChange();
            this.onViewAllChartOptionsClick();
            this.onPickerClick();
            this.initAutoNumberic();
        }, 150);
    }

    private getAccountCheckBoxHtml(): string {
        let html = '';
        if (this.accounts?.length) {
            let ind = 1;
            for (const account of this.accounts) {
                const shouldCheckClass = this.base.tiChartSeries?.hiddenAccounts?.includes(account.privacyName) ? '' : 'checked';
                html += `
                <div class="checkbox-input account" data-value="${account.privacyName}">
                    <input type="checkbox" class="mgr-8" id="account-${ind}" ${shouldCheckClass} />
                    <label for="account-${ind}">${account.privacyName}</label>
                </div>`;

                ind++;
            }
        }

        return html;
    }

    private onNextResistanceAndSupportChange() {
        const nextResistanceAndSupportCheckbox = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-resistance-and-support');
        nextResistanceAndSupportCheckbox?.addEventListener('change', () => {
            this.setRightEdgePaddingCheckboxState();
        });
    }

    private setRightEdgePaddingCheckboxState() {
        const rightEdgePaddingCheckbox = this.popup?.querySelector<HTMLInputElement>('#chart-settings-enable-right-edge-padding');
        if (!rightEdgePaddingCheckbox) {
            return;
        }
        
        const disableRightEdgePaddingCheckbox = this.hasAnyLimitOrders || this.hasAnyPositions;
        if (disableRightEdgePaddingCheckbox) {
            rightEdgePaddingCheckbox.setAttribute('disabled', 'true');
            rightEdgePaddingCheckbox.checked = true;
        } else {
            rightEdgePaddingCheckbox.removeAttribute('disabled');
        }
    }

    private onTabClick() {
        const tabHeaders = this.popup?.querySelectorAll<HTMLElement>('.tabs .tab') ?? [];
        const tabContents = this.popup?.querySelectorAll<HTMLElement>('.content .content-tab') ?? [];
        tabHeaders.forEach((el: HTMLElement, i: number) => {
            el.addEventListener('click', () => {
                let height = 0;
                tabHeaders.forEach((tab: HTMLElement) => {
                    tab.classList.remove('active');
                });
                el.classList.add('active');
                tabContents.forEach((content: HTMLElement, j: number) => {
                    if (j == 0) {
                        height = content.clientHeight ?? 0;
                    } else {
                        if (height > 0) {
                            content.style.height = height + 'px';
                        }
                    }

                    content.style.display = i === j ? content.addClass('active') : content.removeClass('active');
                });
            });
        });
    }

    private onViewAllChartOptionsClick() {
        const viewAllChartOptionsElement = this.popup?.querySelector<HTMLElement>('.view-all-chart-options');
        if (viewAllChartOptionsElement) {
            viewAllChartOptionsElement.addEventListener('click', () => {
                this.base.chartService.requestOpenAllChartOptions();
            });
        }
    }

    private initAutoNumberic() {
        const quoteQuantityInputElement = this.popup?.querySelector<HTMLElement>('.default-trade-size input');

        if (quoteQuantityInputElement) {
            const model = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
            this.quoteQuantityInput = new AutoNumeric(quoteQuantityInputElement, model?.defaultQuoteQuantity ?? 1000, {
                decimalPlaces: 0
            });
        }
    }

    private onBodyClick() {
        this.base.domEvents.onBodyClick(this.id, (ev: any) => {
            const target = ev.target as HTMLElement;
            if (target.classList.contains('font-size-picker-wrapper') || !!target.closest('.font-size-picker-wrapper')) {
                return;
            }

            this.closePicker();
        });
    }

    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            this.setContentHeight();
        });
    }

    private setContentHeight() {
        this.popup?.findFirst('.content')?.setAttribute('style', `overflow-y: auto; max-height: ${this.base.chartWrapper?.clientHeight - 150}px`);
    }
}